import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate, todayLocal } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, FileDown, Receipt, BadgeDollarSign } from "lucide-react";
import { Modal, In, Sel } from "@/pages/Projects";

const STATUS = {
  borrador: ["BORRADOR", "#94a3b8"],
  presentada: ["PRESENTADA", "#22d3ee"],
  autorizada: ["AUTORIZADA", "#16a34a"],
  facturada: ["FACTURADA", "#f97316"],
  cobrada: ["COBRADA", "#22c55e"],
  pagada: ["PAGADA", "#3F2A91"],
  rechazada: ["RECHAZADA", "#dc2626"],
};

export default function EstimacionesControlTab({ projectId, user }) {
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("");
  const [ests, setEsts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [openGen, setOpenGen] = useState(false);
  const [genForm, setGenForm] = useState({ period_start: "", period_end: todayLocal(), iva_pct: 16 });
  const [linkModal, setLinkModal] = useState(null);
  const [linkInvoiceId, setLinkInvoiceId] = useState("");
  const [saving, setSaving] = useState(false);
  const canManage = ["admin", "contador"].includes(user?.role);

  useEffect(() => {
    api.get("/contracts", { params: { project_id: projectId } }).then(({ data }) => {
      const list = (Array.isArray(data) ? data : []).filter((c) => c.project_id === projectId);
      setContracts(list);
      setContractId(list[0]?.id || "");
    });
  }, [projectId]);

  const load = async () => {
    if (!contractId) { setEsts([]); return; }
    const { data } = await api.get(`/contracts/${contractId}/estimaciones`);
    setEsts(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contractId]);

  const openLink = async (e) => {
    setLinkModal(e);
    setLinkInvoiceId("");
    try {
      const { data } = await api.get("/client-invoices");
      setInvoices((data || []).filter((i) => !i.contract_id || i.contract_id === contractId));
    } catch { setInvoices([]); }
  };

  const generate = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/contracts/${contractId}/estimaciones/from-generators`, {
        period_start: genForm.period_start, period_end: genForm.period_end, iva_pct: Number(genForm.iva_pct),
      });
      let msg = `Estimación #${data.estimacion.number} creada con ${data.generators_linked} generadores`;
      if (data.capped?.length) msg += ` · ${data.capped.length} concepto(s) con excedente excluido`;
      toast.success(msg, { duration: 7000 });
      (data.warnings || []).forEach((w) => toast.warning(w, { duration: 9000 }));
      if (data.excluded?.length) toast.warning(`${data.excluded.length} grupo(s) excluidos (revisa autorizaciones)`, { duration: 7000 });
      setOpenGen(false); load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Error");
    }
    setSaving(false);
  };

  const linkInvoice = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      await api.post(`/estimaciones/${linkModal.id}/link-invoice`, { invoice_id: linkInvoiceId });
      toast.success("Factura ligada: estimación FACTURADA");
      setLinkModal(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const markCollected = async (e) => {
    const reference = window.prompt(`Referencia del cobro de la estimación #${e.number} (opcional):`);
    if (reference === null) return;
    try {
      await api.post(`/estimaciones/${e.id}/mark-collected`, { reference: reference || null });
      toast.success("Estimación COBRADA");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const downloadExpediente = async (e) => {
    const res = await api.get(`/estimaciones/${e.id}/expediente/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `EXPEDIENTE_EST_${e.number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const chip = (s) => {
    const [label, color] = STATUS[s] || [s, "#94a3b8"];
    return <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm" style={{ background: `${color}22`, color, border: `1px solid ${color}66` }}>{label}</span>;
  };

  return (
    <div className="space-y-3" data-testid="est-control-tab">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="label-mono">Contrato:</span>
          <select data-testid="est-contract-select" value={contractId} onChange={(e) => setContractId(e.target.value)} className="input-cyber px-3 py-1.5 text-sm max-w-[340px]">
            {contracts.length === 0 && <option value="" style={{ background: "#0a0f1d" }}>Sin contratos en esta obra</option>}
            {contracts.map((c) => <option key={c.id} value={c.id} style={{ background: "#0a0f1d" }}>{c.contract_number || c.id.slice(0, 8)} · {fmtMXN(c.total)}</option>)}
          </select>
        </div>
        {contractId && (
          <button data-testid="est-from-generators-btn" onClick={() => setOpenGen(true)} className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Generar del periodo
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500">La estimación se arma con los volúmenes de generadores <b>autorizados</b> del periodo, respetando el tope contratado + convenios. Los excedentes no autorizados se excluyen automáticamente. Edición completa en Contratos → Estimaciones.</p>

      <div className="panel overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]" data-testid="est-control-table">
          <thead className="panel-header"><tr className="text-left">
            <th className="label-mono px-4 py-3">No.</th><th className="label-mono px-4 py-3">Periodo</th>
            <th className="label-mono px-4 py-3 text-right">Subtotal</th><th className="label-mono px-4 py-3 text-right">Neto a cobrar</th>
            <th className="label-mono px-4 py-3">Estado</th><th className="label-mono px-4 py-3">Factura</th>
            <th className="label-mono px-4 py-3">Cobro</th><th className="label-mono px-4 py-3 text-right">Acciones</th>
          </tr></thead>
          <tbody>
            {ests.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-slate-500">Sin estimaciones en este contrato.</td></tr>}
            {ests.map((e) => (
              <tr key={e.id} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                <td className="px-4 py-3 font-mono font-bold" style={{ color: "var(--cyan)" }}>#{e.number}</td>
                <td className="px-4 py-3 font-mono text-xs">{e.period_start} → {e.period_end}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">{fmtMXN(e.subtotal_period)}</td>
                <td className="px-4 py-3 text-right font-mono text-xs font-bold">{fmtMXN(e.net_to_pay)}</td>
                <td className="px-4 py-3">{chip(e.status)}</td>
                <td className="px-4 py-3 text-xs">{e.invoice_folio ? <span style={{ color: "#f97316" }}>{e.invoice_folio}</span> : "—"}</td>
                <td className="px-4 py-3 text-xs">{e.collected_at ? <span style={{ color: "var(--green)" }}>{fmtDate(e.collected_at)}{e.collected_reference ? ` · ${e.collected_reference}` : ""}</span> : "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`est-expediente-${e.id}`} title="Expediente técnico-financiero" onClick={() => downloadExpediente(e)} className="p-1 rounded-sm hover:bg-white/5 mr-1.5" style={{ color: "var(--text-dim)" }}><FileDown className="w-4 h-4" /></button>
                  {canManage && ["autorizada"].includes(e.status) && (
                    <button data-testid={`est-link-invoice-${e.id}`} onClick={() => openLink(e)} className="text-xs font-semibold hover:underline mr-2 inline-flex items-center gap-1" style={{ color: "#f97316" }}><Receipt className="w-3.5 h-3.5" /> FACTURAR</button>
                  )}
                  {canManage && ["facturada", "autorizada"].includes(e.status) && (
                    <button data-testid={`est-collect-${e.id}`} onClick={() => markCollected(e)} className="text-xs font-semibold hover:underline inline-flex items-center gap-1" style={{ color: "var(--green)" }}><BadgeDollarSign className="w-3.5 h-3.5" /> COBRADA</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openGen && (
        <Modal onClose={() => setOpenGen(false)} title="Generar estimación desde generadores">
          <form onSubmit={generate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <In label="Inicio del periodo" testId="gen-period-start" type="date" value={genForm.period_start} onChange={(v) => setGenForm({ ...genForm, period_start: v })} />
              <In label="Fin del periodo" testId="gen-period-end" type="date" value={genForm.period_end} onChange={(v) => setGenForm({ ...genForm, period_end: v })} />
            </div>
            <In label="% IVA" testId="gen-iva" type="number" value={genForm.iva_pct} onChange={(v) => setGenForm({ ...genForm, iva_pct: v })} />
            <p className="text-xs text-slate-400">Incluye generadores <b>autorizados por Dirección</b> del periodo que no estén en otra estimación. Extraordinarios solo si tienen autorización del cliente registrada. Se crea en borrador para revisión.</p>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenGen(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="confirm-from-generators-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">Generar borrador</button>
            </div>
          </form>
        </Modal>
      )}

      {linkModal && (
        <Modal onClose={() => setLinkModal(null)} title={`Ligar factura · Estimación #${linkModal.number}`}>
          <form onSubmit={linkInvoice} className="space-y-4">
            <Sel label="Factura CFDI (Serie C)" testId="link-invoice-select" value={linkInvoiceId} onChange={setLinkInvoiceId}
              options={[{ value: "", label: "— Selecciona —" }, ...invoices.map((i) => ({ value: i.id, label: `${i.folio || i.id.slice(0, 8)} · ${fmtMXN(i.total)}` }))]} />
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setLinkModal(null)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" disabled={saving || !linkInvoiceId} data-testid="confirm-link-invoice-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">Ligar factura</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
